import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Grid, TextField, Typography } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { useMutation } from '@apollo/client';
import { saveMachineTemplate, updateMachineTemplate } from 'apollo/templates/organization/mutation.graphql';
import { machinesTemplate } from 'apollo/templates/organization/query.graphql';
import { Machine } from 'models/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import TimeInput from 'components/shared/time-input';

type AddEditMachineDialogProps = {
    machine?: Machine;
    open: boolean;
    onDialogClose: () => void;
};
const AddEditMachineDialog: FC<AddEditMachineDialogProps> = ({ machine, open, onDialogClose }) => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);
    const { setAppToasterContent } = useContext(AppToasterContext);

    const [name, setName] = useState('');
    const [defaultMachineTime, setDefaultMachineTime] = useState<number | undefined>(undefined);
    const [machineSchedule, setMachineSchedule] = useState<string[]>([]);
    const [scheduleTimeInput, setScheduleTimeInput] = useState<number | undefined>(undefined);
    const [scheduleInputKey, setScheduleInputKey] = useState(0);
    const scheduleInputRef = useRef<HTMLInputElement>(null);
    const [isTimeInputValid, setIsTimeInputValid] = useState(true);

    useEffect(() => {
        scheduleInputRef.current?.focus();
    }, [scheduleInputKey]);

    const refetchQueries = [{ query: machinesTemplate }];
    const [saveMachine, { loading: saveLoading }] = useMutation(saveMachineTemplate, { refetchQueries });
    const [updateMachine, { loading: updateLoading }] = useMutation(updateMachineTemplate, { refetchQueries });

    useEffect(() => {
        setName(machine?.name ?? '');
        setDefaultMachineTime(machine?.defaultMachineTime);
        setMachineSchedule(machine?.machineSchedule ?? []);
        setScheduleTimeInput(undefined);
        setIsTimeInputValid(true);
    }, [machine, open]);

    const isEdit = !!machine?._id;
    const isLoading = saveLoading || updateLoading;
    const isSaveDisabled = !name.trim() || !isTimeInputValid || isLoading;

    const handleAddScheduleTime = () => {
        if (scheduleTimeInput === undefined) return;
        const timeString = decimalToTimeString(scheduleTimeInput);
        if (machineSchedule.includes(timeString)) return;
        setMachineSchedule((prev) => [...prev, timeString].sort());
        setScheduleTimeInput(undefined);
        setScheduleInputKey((prev) => prev + 1);
    };

    const decimalToTimeString = (decimal: number): string => {
        const hours = Math.floor(decimal);
        const minutes = Math.round((decimal - hours) * 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    const handleRemoveScheduleTime = (time: string) => {
        setMachineSchedule((prev) => prev.filter((t) => t !== time));
    };

    const handleSave = async () => {
        const machineData = {
            _id: machine?._id,
            name: name.trim(),
            defaultMachineTime,
            machineSchedule,
        };
        if (isEdit) {
            await updateMachine({ variables: { machine: machineData } });
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:MACHINE_UPDATED_SUCCESSFULLY'),
            });
        } else {
            await saveMachine({ variables: { machine: machineData } });
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('lab-settings:MACHINE_ADDED_SUCCESSFULLY'),
            });
        }
        onDialogClose();
    };

    return (
        <DHDialog
            open={open}
            onClose={onDialogClose}
            fullWidth
            maxWidth="sm"
            title={isEdit ? translate('lab-settings:EDIT_MACHINE') : translate('lab-settings:ADD_MACHINE')}
            actions={
                <Button variant="contained" disabled={isSaveDisabled} onClick={handleSave}>
                    {translate('common:SAVE')}
                </Button>
            }
        >
            <Grid container spacing={1} mt={1}>
                <Grid item xs={7}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label={translate('lab-settings:MACHINE_NAME')}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </Grid>
                <Grid item xs={5}>
                    <TimeInput
                        fullWidth
                        size="small"
                        label={translate('lab-settings:DEFAULT_MACHINE_TIME')}
                        value={defaultMachineTime}
                        onChange={setDefaultMachineTime}
                        onValidChange={setIsTimeInputValid}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="h5" my={1}>
                        {translate('lab-settings:MACHINE_SCHEDULE')}
                    </Typography>
                    <Box display="flex" alignItems="center" mb={1}>
                        <TimeInput
                            key={scheduleInputKey}
                            size="small"
                            label={translate('lab-settings:ADD_SCHEDULE_TIME')}
                            value={scheduleTimeInput}
                            onChange={setScheduleTimeInput}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddScheduleTime()}
                            inputRef={scheduleInputRef}
                            sx={{ width: 140 }}
                        />
                        <Button size="small" disabled={scheduleTimeInput === undefined} onClick={handleAddScheduleTime}>
                            {translate('common:ADD')}
                        </Button>
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                        {machineSchedule.map((time) => (
                            <Chip
                                key={time}
                                label={time}
                                onDelete={() => handleRemoveScheduleTime(time)}
                                size="small"
                            />
                        ))}
                    </Box>
                </Grid>
            </Grid>
        </DHDialog>
    );
};

export default AddEditMachineDialog;
